import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/motion";
import { SERVICES, PROCESS, CONTACT, waLink } from "@/data/site";
import { ArrowRight, Diamond } from "lucide-react";
import { Link } from "react-router-dom";

export default function Services() {
  return (
    <main data-testid="services-page">
      <PageHero
        testid="services-hero"
        overline="Services"
        lines={["Brokerage, logistics,", "assaying —", "end to end."]}
        intro="One mandate, three disciplines. We find the counterparty, prove the product, and move it from pit to port."
      />

      <section data-testid="services-list" className="mx-auto max-w-7xl space-y-24 px-6 pb-28 lg:px-10">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id}>
            <div
              data-testid={`service-${s.id}`}
              className={`grid items-center gap-10 lg:grid-cols-12 ${i % 2 === 1 ? "" : ""}`}
            >
              <div className={`relative overflow-hidden rounded-[2rem] border border-white/10 lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img src={s.image} alt={s.title} loading="lazy" className="h-72 w-full object-cover lg:h-96" />
                <div className="absolute inset-0 spotlight opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent" />
                <span className="absolute bottom-6 left-6 font-serif text-5xl gold-text">{s.n}</span>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <Overline>Service {s.n}</Overline>
                <h2 className="mt-4 font-serif text-3xl tracking-tight text-slate-50 sm:text-4xl">{s.title}</h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">{s.description}</p>
                <ul className="mt-7 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm uppercase tracking-[0.18em] text-slate-300">
                      <Diamond className="h-2.5 w-2.5 text-gold" fill="currentColor" /> {p}
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(CONTACT.phones[1].wa, `Hello Tuskers Grey Investments — I'd like to discuss your ${s.title} service.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-enquire-${s.id}`}
                  className="group mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:text-gold-light"
                >
                  Discuss this service <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section data-testid="process-section" className="border-t hairline-gold bg-navy-900/40">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <Overline>From enquiry to settlement</Overline>
            <h2 className="mt-5 font-serif text-3xl tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Four steps. No shortcuts.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 md:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="h-full">
                <div data-testid={`process-step-${p.n}`} className="group flex h-full flex-col bg-navy-950/80 p-8 transition-colors duration-500 hover:bg-navy-900">
                  <span className="font-serif text-4xl text-gold/30 transition-colors duration-500 group-hover:text-gold">{p.n}</span>
                  <h3 className="mt-6 font-serif text-xl text-slate-100">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <Link
                to="/contact"
                data-testid="services-contact-button"
                className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-navy-950 transition-all duration-300 hover:bg-gold-light"
              >
                Start a mandate <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
